import React from "react";
import { Zap, Users, Palette, Film } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "01. OFFLINE & ONLINE EDIT",
  description: "Precision offline and online editing shapes your story with expert cuts, fluid transitions, and impeccable rhythm that define its visual and emotional power.",
  icon: Zap,
  color: "text-primary"
}, {
  title: "02. COLOR GRADING",
  description: "Expert color grading and creative look development sculpt the visual narrative and emotional tone, defining your story’s unforgettable cinematic voice.",
  icon: Palette,
  color: "text-primary"
}, {
  title: "03.\xA0 MOTION GRAPHICS",
  description: "Dynamic motion graphics command attention with immersive design and fluid animation, elevating your film’s visual storytelling to new heights.",
  icon: Users,
  color: "text-primary"
}, {
  title: "04. 2D & 3D ANIMATION",
  description: "Masterful 2D and 3D animation elevates premium CAD models, prototypes, and previs sequences into breathtaking product visuals with fluid, cinematic precision.",
  icon: Film,
  color: "text-primary"
}, {
  title: "05. SCRIPT CONSULTATION",
  description: "Expert narrative development and script consultation strengthen structure and emotional impact for stories with universal, lasting resonance.",
  icon: Zap,
  color: "text-primary"
}, {
  title: "06. GRAPHIC DESIGN",
  description: "Bespoke graphic design creates distinctive visual identities and compelling key art that authentically capture your production’s brand essence.",
  icon: Palette,
  color: "text-primary"
}];
export default function TheDesignFluxEdge() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="10381" id="core-expertise" className="dark bg-background text-foreground relative overflow-hidden py-24 px-6 md:px-12">
      {}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-[140px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/40 rounded-full blur-[140px]" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto">
        <div className={cn("mb-16 text-left transition-all duration-700 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-5xl md:text-8xl font-bold tracking-tighter uppercase font-serif bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/70">
            Core Expertise
          </h2>
          <p className="mt-6 text-xl font-sans text-muted-foreground max-w-2xl">
            Cinematic precision across every frame.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {features.map((feature, i) => <div key={i} data-index={i} className={cn("transition-all duration-700 ease-out delay-[100ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="h-full border-0 border-b border-primary bg-transparent backdrop-blur-none shadow-none hover:bg-white/5 transition-colors duration-400 rounded-none overflow-hidden group">
                <CardHeader className="pb-2 pt-8 px-8">
                  <div className="mb-6 inline-flex items-center justify-center p-0 rounded-none bg-transparent border-0 shadow-none group-hover:scale-105 transition-transform duration-300 w-fit">
                    <feature.icon className={cn("w-6 h-6", feature.color)} strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-2xl font-bold font-serif leading-tight tracking-tight uppercase">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-12">
                  <p className="text-muted-foreground font-sans leading-relaxed text-lg">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}